import { CashlessRepository } from "Infrastructure/repositories/api/CashlessRepository";
import { useKiosk } from "application/contexts/kiosk/KioskContext";
import { useUi } from "application/contexts/ui/UIContext";
import BottomModal from "components/bottomModal/BottomModal";
import { Keyboard } from "components/keyboard/Keyboard";
import { PassKeyboard } from "components/passKeyboard/PassKeyboard";
import { TransactionType } from "domains/order/agreggates/payment/transactionType";
import { DoCashlessCosumeUseCase } from "modules/order/application/useCases/DoCashlessCosumeUseCase";
import { ISyncNominalOrderResult } from "modules/order/application/useCases/ISyncNominalOrderResult";
import { ICustomer } from "modules/order/domain/models/ICustomer";
import { IOrder } from "modules/order/domain/models/IOrder";
import { ICardCashless } from "modules/payment/domain/models/ICashless";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Trans } from "react-i18next";
import { createDefferedPromise } from "utils/DefferedPromise";
import styles from "./NominalCashless.module.scss";

const cashlessRepository = CashlessRepository();

export interface INominalCashlessHandler {
  open(
    order: IOrder,
    cardData: ICardCashless,
  ): Promise<ISyncNominalOrderResult>;
}

interface ICashlessProps {}

const UseNominalCashless: React.ForwardRefRenderFunction<
  INominalCashlessHandler,
  ICashlessProps
> = (_, ref) => {
  const deffered = useRef(createDefferedPromise<ISyncNominalOrderResult>());
  const { toast, showLoading, hideLoading } = useUi();

  const [visible, setVisible] = useState(false);
  const [order, setOrder] = useState<IOrder>({} as IOrder);
  const [password, setPassword] = useState("");
  const [cardData, setCardData] = useState<ICardCashless>();
  const { kiosk } = useKiosk();

  const open = useCallback((_order: IOrder, _cardData: ICardCashless) => {
    setOrder(_order);
    setVisible(true);
    setCardData(_cardData);
    deffered.current = createDefferedPromise<ISyncNominalOrderResult>();
    return deffered.current.promise;
  }, []);

  useImperativeHandle(ref, () => ({ open }));

  const onKeyPress = useCallback(
    (key: string) => {
      if (password.length < 4) {
        setPassword(password + key);
      }
    },
    [password],
  );
  const onClearPress = useCallback(() => {
    setPassword("");
  }, []);

  const onRemovePress = useCallback(() => {
    setPassword((prev) => prev.substring(0, prev.length - 1));
  }, []);

  useEffect(() => {
    if (!visible) {
      setPassword("");
    }
  }, [visible]);

  const onConfirmPassword = useCallback(
    async (validated?: boolean) => {
      if (cardData?.password === password || validated) {
        if (!kiosk?.localId) {
          toast("Falha ao efetuar cobrança no cartão", "error");
          setVisible(false);
          deffered.current.rejecter("Falha no local corrente");
        } else {
          const customer: ICustomer = {
            ...order.customer,
            document: cardData?.holderDocument,
            name: cardData?.holderName,
            associationId: cardData?.associationId,
            tag: cardData?.number,
          };

          const orderWithCustomer: IOrder = {
            ...order,
            customer,
          };

          showLoading();

          const syncResult = await DoCashlessCosumeUseCase(
            cashlessRepository,
            orderWithCustomer,
            TransactionType.cashless,
            kiosk,
          );

          hideLoading();

          if (!syncResult.success) {
            setVisible(false);
            deffered.current.rejecter(
              syncResult?.message ?? "Falha ao sincronizar consumo",
            );
          }

          setVisible(false);
          deffered.current.resolver({
            ...syncResult,
            order: orderWithCustomer,
          });
        }
      } else {
        toast("Digitos não correspondem", "error");
        setPassword("");
      }
    },
    [cardData, hideLoading, kiosk, order, password, showLoading, toast],
  );

  const onCancel = useCallback(() => {
    setVisible(false);
    setPassword("");
    deffered.current?.rejecter(
      new Error("Fechado pelo usuário ou tempo esgotado"),
    );
  }, []);

  return (
    <BottomModal showModal={visible} onClickClose={onCancel}>
      <div className={styles.passwordContainer}>
        <h2>
          <Trans i18nKey="cashless.nominal.password_prompt">
            Digite sua <b>senha </b>
          </Trans>
        </h2>
        {!kiosk?.useStrongPassword && (
          <div className={styles.input}>{password.replace(/[0-9]/g, "*")}</div>
        )}
        {kiosk?.useStrongPassword ? (
          <PassKeyboard
            onValidate={onConfirmPassword}
            password={cardData?.password ?? "xxx"}
          />
        ) : (
          <Keyboard
            onKeyPress={onKeyPress}
            onPressClear={onClearPress}
            onPressBack={onRemovePress}
            onPressEnter={onConfirmPassword}
            type="numeric"
          />
        )}
      </div>
    </BottomModal>
  );
};

export default forwardRef(UseNominalCashless);
